import React, { useState, useMemo, useEffect } from "react";
import placeholder from "../../assets/Car-place-holder-PNG.png";

export default function CarImageGallery({ images, watchingNow = 0, highlight = null }) {
  const [selectedImage, setSelectedImage] = useState(placeholder);

  // Build an array of URL strings from mixed API shapes
  const imageUrls = useMemo(() => {
    if (!images) return [];
    return images
      .map((img) =>
        typeof img === "string"
          ? img
          : img?.imageUrl || img?.url || img?.path || img?.src || ""
      )
      .filter(Boolean);
  }, [images]);

  // Update selected image when new data arrives
  useEffect(() => {
    if (imageUrls.length) setSelectedImage(imageUrls[0]);
    else setSelectedImage(placeholder);
  }, [imageUrls]);

  return (
    <div className='w-full shadow-md max-w-7xl mx-auto'>
      <div className='relative'>
        <img
          src={selectedImage}
          alt='car'
          className={`w-full h-[400px] object-cover ${selectedImage === placeholder ? 'opacity-50' : ''}`}
          onError={(e)=>e.currentTarget.src=placeholder}
        />
        
        {/* Watching Now Badge - Top Right Corner */}
        {watchingNow > 0 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span className="font-semibold text-sm">
              Watching Now: <span className="font-bold">{watchingNow}</span>
            </span>
          </div>
        )}

        {/* Highlight Badge - Bottom Left Corner */}
        {highlight && (
          <div className="absolute bottom-4 left-4 bg-primary text-white px-4 py-2 rounded-lg shadow-lg">
            <span className="font-bold text-sm uppercase tracking-wide">
              {highlight}
            </span>
          </div>
        )}

        {/* Previous Button */}
        {imageUrls.length > 1 && (
          <button
            className='absolute left-2 top-1/2 -translate-y-1/2 bg-transparent rounded-full shadow p-2'
            onClick={() => {
              const idx = imageUrls.indexOf(selectedImage);
              setSelectedImage(
                imageUrls[(idx - 1 + imageUrls.length) % imageUrls.length]
              );
            }}
            aria-label='Previous Image'
          >
            {/* Left Arrow SVG */}
            <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
              <path
                d='M15 6l-6 6 6 6'
                stroke='#ffffff'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}
        {/* Next Button */}
        {imageUrls.length > 1 && (
          <button
            className='absolute right-2 top-1/2 -translate-y-1/2 bg-transparent rounded-full shadow p-2'
            onClick={() => {
              const idx = imageUrls.indexOf(selectedImage);
              setSelectedImage(imageUrls[(idx + 1) % imageUrls.length]);
            }}
            aria-label='Next Image'
          >
            {/* Right Arrow SVG */}
            <svg width='24' height='24' fill='none' viewBox='0 0 24 24'>
              <path
                d='M9 6l6 6-6 6'
                stroke='#ffffff'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}
      </div>
      <div className='grid grid-cols-4 md:grid-cols-6 gap-2 mt-3 max-h-[120px] overflow-y-auto custom-scrollbar px-3 pb-3'>
        {imageUrls.length > 0 ? (
          imageUrls.map((img, index) => (
            <img
              key={index}
              src={img || placeholder}
              alt={`thumb-${index}`}
              onClick={() => setSelectedImage(img)}
              className={`h-[80px] w-full object-cover cursor-pointer border-2 rounded ${
                selectedImage === img
                  ? "border-primary"
                  : "border-gray-700"
              }`}
              onError={(e)=>e.currentTarget.src=placeholder}
            />
          ))
        ) : (
          <img
            src={placeholder}
            alt='placeholder'
            className='h-[80px]  w-full object-cover border-2 border-gray-700 rounded opacity-50'
          />
        )}
      </div> 
    </div>
  );
}